import React, { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import { optionsAPI } from '../../services/api';

// ─── Payment Method Select ────────────────────────────────────────────────────
const DEFAULT_METHODS = [
  { value: 'cash',            label: 'Cash' },
  { value: 'upi',             label: 'UPI' },
  { value: 'bank_transfer',   label: 'Bank Transfer' },
  { value: 'cheque',          label: 'Cheque' },
  { value: 'payment_gateway', label: 'Payment Gateway' },
  { value: 'coupon',          label: 'Coupon' },
];

export default function PaymentMethodSelect({ value, onChange, style, className }) {
  const [methods,  setMethods]  = useState(DEFAULT_METHODS);
  const [adding,   setAdding]   = useState(false);
  const [newName,  setNewName]  = useState('');
  const [saving,   setSaving]   = useState(false);
  const inputRef = React.useRef(null);

  useEffect(() => {
    optionsAPI.get('paymentMethods')
      .then(({ data }) => {
        const custom = (data.data || []).map(s => String(s).trim()).filter(Boolean);
        const defaults = DEFAULT_METHODS.map(m => m.value);
        const extra = custom.filter(s => !defaults.includes(s)).map(s => ({ value: s, label: s }));
        setMethods([...DEFAULT_METHODS, ...extra]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (adding && inputRef.current) inputRef.current.focus();
  }, [adding]);

  const saveNew = async () => {
    const n = newName.trim();
    if (!n) return;
    setSaving(true);
    try {
      await optionsAPI.add('paymentMethods', n);
      const newMethod = { value: n, label: n };
      setMethods(prev => [...prev, newMethod]);
      onChange(n);
      setNewName('');
      setAdding(false);
    } catch { /* keep open */ }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6 }}>
        <select className={className} style={{ ...style, flex: 1 }} value={value} onChange={e => onChange(e.target.value)}>
          {methods.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <button
          type="button"
          title="Add payment mode"
          onClick={() => { setAdding(a => !a); setNewName(''); }}
          style={{
            flexShrink: 0, width: 32, borderRadius: 5,
            border: `1.5px solid ${adding ? 'var(--accent)' : 'var(--cb-d1d5db)'}`,
            background: adding ? 'var(--cb-e0f2fe)' : 'transparent',
            color: adding ? 'var(--c-0284c7)' : 'var(--c-6b7280)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Plus size={14} />
        </button>
      </div>
      {adding && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <input
            ref={inputRef}
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); saveNew(); } if (e.key === 'Escape') { setAdding(false); setNewName(''); } }}
            placeholder="New mode name…"
            style={{ flex: 1, border: '1px solid var(--cb-93c5fd)', borderRadius: 5, padding: '4px 8px', fontSize: '.83rem', outline: 'none' }}
          />
          <button type="button" onClick={saveNew} disabled={saving || !newName.trim()}
            style={{ padding: '4px 10px', borderRadius: 5, border: 'none', background: 'var(--accent)', color: 'var(--c-ffffff)', cursor: 'pointer', fontSize: '.82rem', fontWeight: 600, opacity: !newName.trim() ? 0.5 : 1 }}>
            {saving ? '…' : 'Add'}
          </button>
          <button type="button" onClick={() => { setAdding(false); setNewName(''); }}
            style={{ padding: '4px 8px', borderRadius: 5, border: '1px solid var(--cb-e5e7eb)', background: 'transparent', cursor: 'pointer', color: 'var(--c-6b7280)' }}>
            <X size={13} />
          </button>
        </div>
      )}
    </div>
  );
}
